import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import type React from "react";
import andersImg from "@/assets/anders-portrait.jpg";
import angelaImg from "@/assets/angela-portrait.png";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About SaaSed — Salesforce Procurement & Commercial Advisory" },
      { name: "description", content: "Meet the SaaSed team: independent Salesforce procurement specialists supporting enterprise renewals, licensing and commercial negotiations." },
      { property: "og:title", content: "About SaaSed — Salesforce Procurement & Commercial Advisory" },
      { property: "og:description", content: "Meet the SaaSed team: independent Salesforce procurement specialists supporting enterprise renewals, licensing and commercial negotiations." },
      { name: "twitter:description", content: "Meet the SaaSed team: independent Salesforce procurement specialists supporting enterprise renewals, licensing and commercial negotiations." },

      { property: "og:image", content: "https://saased.co/saased-share-image.png" },
      { name: "twitter:image", content: "https://saased.co/saased-share-image.png" },
      { property: "og:url", content: "https://saased.co/about" },
    ],
    links: [{ rel: "canonical", href: "https://saased.co/about" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "@id": "https://saased.co/about#webpage",
          "url": "https://saased.co/about",
          "name": "About SaaSed — Salesforce Procurement & Commercial Advisory",
          "description": "SaaSed is a specialist Salesforce procurement and commercial advisory company. We work with enterprise buyers on Salesforce renewals, contract negotiation, licensing decisions, commercial strategy and cost optimisation — with the aim of improving commercial control and removing cost that serves no purpose.",
          "isPartOf": { "@id": "https://saased.co/#website" },
          "about": { "@id": "https://saased.co/#organization" },
          "mainEntity": { "@id": "https://saased.co/#organization" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({"@context":"https://schema.org","@type":"FAQPage","@id":"https://saased.co/about#faq","mainEntity":[{"@type":"Question","name":"Is SaaSed independent of Salesforce?","acceptedAnswer":{"@type":"Answer","text":"SaaSed is independent of Salesforce: we are not a Salesforce partner or reseller. We work for the buyer, and we aim to deliver the outcome they hired us for while keeping their relationship with Salesforce intact."}},{"@type":"Question","name":"What does SaaSed specialise in?","acceptedAnswer":{"@type":"Answer","text":"Our specialisation is deliberately narrow: Salesforce as a vendor. That means Salesforce renewals, contract negotiation, licensing and cost optimisation, and the enterprise commercial structures behind them, including Salesforce SELA and Salesforce AELA agreements."}},{"@type":"Question","name":"Who does SaaSed work with?","acceptedAnswer":{"@type":"Answer","text":"We typically work with procurement, finance and IT leaders at mid-to-large enterprises in EMEA that run significant Salesforce estates. Most engage us ahead of a renewal, a new enterprise agreement, a mid-term add-on or an internal review of Salesforce spend, while there is still time to change the commercial outcome."}},{"@type":"Question","name":"What problems does SaaSed help solve?","acceptedAnswer":{"@type":"Answer","text":"The problems we help solve are commercial rather than technical: unclear pricing, licence mixes that no longer match how teams work, shelfware, bundled add-ons, and agreement structures that are hard to benchmark or unwind. If you want a complimentary review of your Salesforce contract, that is where we start."}},{"@type":"Question","name":"Why does SaaSed exist?","acceptedAnswer":{"@type":"Answer","text":"Our purpose is for you to sign contracts based on what your business needs right now, not the dream a vendor sells you for later. By actively optimising your operational expenses, our goal is to be your go-to partner for fair tech pricing, protecting your budget so you can reinvest in your actual growth."}},{"@type":"Question","name":"Who is behind SaaSed?","acceptedAnswer":{"@type":"Answer","text":"We are a team of contract negotiators, RFP builders, and implementation tinkerers, who saw that SaaS vendors push licenses, partners collect hours, and buyers end up with shelfware."}}]}),
      },
    ],
  }),
  component: AboutPage,
});


const linkCls = "underline! decoration-[#f5cc00]! decoration-2! underline-offset-4 hover:text-[#1a1408]";

const faqs: { q: string; a: React.ReactNode }[] = [
  { q: "Is SaaSed independent of Salesforce?", a: "SaaSed is independent of Salesforce: we are not a Salesforce partner or reseller. We work for the buyer, and we aim to deliver the outcome they hired us for while keeping their relationship with Salesforce intact." },
  { q: "What does SaaSed specialise in?", a: <>Our specialisation is deliberately narrow: Salesforce as a vendor. That means Salesforce renewals, contract negotiation, licensing and cost optimisation, and the enterprise commercial structures behind them, including <Link to="/sela" className={linkCls}>Salesforce SELA</Link> and <Link to="/aela" className={linkCls}>Salesforce AELA</Link> agreements.</> },
  { q: "Who does SaaSed work with?", a: "We typically work with procurement, finance and IT leaders at mid-to-large enterprises in EMEA that run significant Salesforce estates. Most engage us ahead of a renewal, a new enterprise agreement, a mid-term add-on or an internal review of Salesforce spend, while there is still time to change the commercial outcome." },
  { q: "What problems does SaaSed help solve?", a: "The problems we help solve are commercial rather than technical: unclear pricing, licence mixes that no longer match how teams work, shelfware, bundled add-ons, and agreement structures that are hard to benchmark or unwind. If you want a complimentary review of your Salesforce contract, that is where we start." },
  { q: "Why does SaaSed exist?", a: "Our purpose is for you to sign contracts based on what your business needs right now, not the dream a vendor sells you for later. By actively optimising your operational expenses, our goal is to be your go-to partner for fair tech pricing, protecting your budget so you can reinvest in your actual growth." },
  { q: "Who is behind SaaSed?", a: "We are a team of contract negotiators, RFP builders, and implementation tinkerers, who saw that SaaS vendors push licenses, partners collect hours, and buyers end up with shelfware." },
];

const facts = [
  { n: "01", t: "Salesforce only", d: "Our specialisation is deliberately narrow: Salesforce as a vendor." },
  { n: "02", t: "Independent of Salesforce", d: "Not a Salesforce partner or reseller. We work for the buyer." },
  { n: "03", t: "Built for enterprise buyers", d: "Procurement, finance and IT leaders at mid-to-large enterprises in EMEA." },
];

const summaryCls = "mt-6 cursor-pointer list-none border-t border-[#f0e8c8] pt-4 font-sans text-sm font-medium text-[#1a1408] [&::-webkit-details-marker]:hidden";

function AboutPage() {
  return (
    <>
      {/* HERO */}
      <section className="bg-[#fae396]">
        <div className="container-x py-24 px-6 md:px-12">
          <div className="flex flex-col gap-6 md:gap-8">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">About Us</p>
            <h1 className="max-w-4xl text-5xl font-bold leading-[1] text-[#1a1408] md:text-7xl">Who we are.</h1>
            <div aria-hidden="true" className="h-[2px] w-16 bg-[#f5cc00]" />
            <p className="max-w-3xl text-left text-xl font-medium leading-[1.5] text-[#1a1408] md:text-2xl">
              SaaSed is a specialist Salesforce procurement and commercial advisory company. We work with enterprise buyers on Salesforce renewals, contract negotiation, licensing decisions, commercial strategy and cost optimisation — with the aim of improving commercial control and removing cost that serves no purpose.
            </p>
          </div>
        </div>
      </section>

      {/* FACT CARDS */}
      <section className="container-x flex flex-col gap-12 py-16 md:py-24 px-6 md:px-12">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {facts.map((f) => (
            <div key={f.n} className="flex flex-col gap-3 rounded-2xl bg-[#f0e8c8] p-8">
              <p className="font-sans text-[11px] font-medium uppercase tracking-[0.12em] text-[#1a1408]/70">{f.n}</p>
              <h3 className="font-sans text-[22px] font-medium leading-snug text-[#1a1408]">{f.t}</h3>
              <p className="text-left text-base leading-relaxed text-[#1a1408]/85">{f.d}</p>
            </div>
          ))}
        </div>
        <p className="mx-auto max-w-3xl text-center text-lg text-muted-foreground" style={{ marginLeft: "auto", marginRight: "auto" }}>
          If you are anything like the rest of the SaaS buyers out there today,
          you are overpaying 17% up to 50% in licenses. That adds up to a
          significant amount of dollars.
        </p>
      </section>

      {/* TEAM */}
      <section className="container-x pb-16 md:pb-24 px-6 md:px-12">
        <h2 className="text-3xl font-bold text-[#1a1408] md:text-4xl">Meet our Leadership Team</h2>
        <div aria-hidden="true" className="mt-4 h-[2px] w-16 bg-[#f5cc00]" />
        <div className="mt-10 grid grid-cols-1 items-start gap-8 md:grid-cols-2">
          <div className="@container min-w-0 rounded-2xl border border-[#f0e8c8] bg-[#fffdf0]">
            <div className="flex flex-col p-8 @[560px]:p-10">
            <div className="flex min-h-[330px] flex-col items-start gap-6 @[560px]:min-h-[193px] @[560px]:flex-row @[560px]:gap-7">
              <img src={angelaImg} alt="Portrait of Angela Lai, Co-Founder and CEO of SaaSed" loading="lazy" width={176} height={176} className="h-[160px] w-[160px] shrink-0 rounded-2xl object-cover object-top @[560px]:h-[176px] @[560px]:w-[176px]" />
              <div className="flex w-full min-w-0 flex-col">
                <h3 className="m-0 font-sans text-2xl font-medium break-words [overflow-wrap:anywhere] @[560px]:text-3xl text-[#1a1408]">
                  <Link to="/blog/author/$slug" params={{ slug: "angela-lai" }} className={linkCls}>Angela Lai</Link>
                </h3>
                <p className="m-0 mt-[10px]! text-base text-muted-foreground @[560px]:text-lg">Co-Founder &amp; CEO</p>
                <p className="m-0 mt-2! text-left text-base leading-normal text-[#1a1408]/85">Strategy-led founder in SaaS negotiations, operations and vendor management.</p>
              </div>
            </div>
            <details className="mt-auto! pt-6">
              <summary className={`${summaryCls} mt-0! w-full text-base!`}>Read bio</summary>
              <p className="mt-4 text-left text-base leading-relaxed text-muted-foreground">
                Angela Lai is a strategy‑led founder who builds scalable, commercially intelligent systems—turning SaaS negotiations, operations, and vendor management into predictable, high‑leverage engines for growth. She runs her business with a blend of analytical discipline and creative execution, aligning people, processes, and commercial strategy so the company can move fast without losing clarity or control.
              </p>
            </details>
            </div>
          </div>
          <div className="@container min-w-0 rounded-2xl border border-[#f0e8c8] bg-[#fffdf0]">
            <div className="flex flex-col p-8 @[560px]:p-10">
            <div className="flex min-h-[330px] flex-col items-start gap-6 @[560px]:min-h-[193px] @[560px]:flex-row @[560px]:gap-7">
              <img src={andersImg} alt="Portrait of Anders Gustafsson, Co-Founder of SaaSed" loading="lazy" width={176} height={176} className="h-[160px] w-[160px] shrink-0 rounded-2xl object-cover object-[50%_50%] @[560px]:h-[176px] @[560px]:w-[176px]" />
              <div className="flex w-full min-w-0 flex-col">
                <h3 className="m-0 font-sans text-2xl font-medium break-words [overflow-wrap:anywhere] @[560px]:text-3xl text-[#1a1408]">
                  <Link to="/blog/author/$slug" params={{ slug: "anders-gustafsson" }} className={linkCls}>Anders Gustafsson</Link>
                </h3>
                <p className="m-0 mt-[10px]! text-base text-muted-foreground @[560px]:text-lg">Co-Founder &amp; Salesforce Expert</p>
                <p className="m-0 mt-2! text-left text-base leading-normal text-[#1a1408]/85">8 years inside Salesforce. 16+ years in B2B SaaS sales.</p>
              </div>
            </div>
            <details className="mt-auto! pt-6">
              <summary className={`${summaryCls} mt-0! w-full text-base!`}>Read bio</summary>
              <p className="mt-4 text-left text-base leading-relaxed text-muted-foreground">
                16+ years of experience in B2B SaaS sales, working in both scale-up
                and enterprise matrix-style organisations with multi-product KPIs.
                Spent 8 years working for Salesforce before starting SaaSed, where
                he worked as an individual contributor and in second-line
                management. Held various leadership roles in Asia and Northern Europe.
              </p>
              <p className="mt-4 text-sm font-medium text-[#1a1408]">
                <Link to="/blog/author/$slug" params={{ slug: "anders-gustafsson" }} className={linkCls}>Articles by Anders</Link>
              </p>
            </details>
            </div>
          </div>
        </div>
      </section>

      {/* CORE VALUE + 2-1 MODEL */}
      <section className="container-x pb-16 md:pb-24 px-6 md:px-12">
        <div className="grid grid-cols-1 gap-10 rounded-2xl bg-[#f0e8c8] p-8 md:grid-cols-2 md:gap-14 md:p-14">
          <div className="flex flex-col gap-4">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#1a1408]/70">Our core value</p>
            <p className="text-left text-2xl leading-snug text-[#1a1408] md:text-3xl">
              We prioritize long-term relationship over short-term gains.
            </p>
          </div>
          <div className="flex flex-col gap-4">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#1a1408]/70">2-1 model</p>
            <h2 className="text-3xl font-bold text-[#1a1408] md:text-4xl">2% of equity.<br />1% of our time.</h2>
            <p className="text-left text-lg leading-relaxed text-[#1a1408]/85">
              Inspired by Patagonia's 1% for the Planet and Salesforce's 1-1-1
              model, we have adapted a similar pledge. We pledge 2% of our
              equity and 1% of our time to doing a small part to help the world
              be a better, sustainable place.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#f0e8c8]">
        <div className="container-x py-24 md:py-28">
          <h2 className="m-0 text-center font-serif text-3xl font-bold text-[#1a1408] md:text-4xl">
            FAQ about SaaSed
          </h2>
          <div aria-hidden="true" className="mx-auto mt-4 h-[2px] w-16 bg-[#f5cc00]" />
          <div className="mx-auto mt-12 flex max-w-3xl flex-col gap-4">
            {faqs.map((f, i) => (
              <details key={f.q} open={i === 0} className="group overflow-hidden rounded-2xl border border-[#f0e8c8] bg-[#fffdf0] shadow-[0_1px_2px_rgba(26,20,8,0.06)] transition-colors duration-150 hover:border-[#1a1408]/30">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-6 py-5 md:px-8 md:py-6 rounded-2xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1a1408] [&::-webkit-details-marker]:hidden">
                  <h3 className="m-0 font-sans text-lg font-medium leading-[1.35] tracking-normal text-[#1a1408] md:text-xl">{f.q}</h3>
                  <span aria-hidden="true" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f0e8c8] transition-colors duration-200 group-open:bg-[#f5cc00]">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="transition-transform duration-200 group-open:rotate-45"><path d="M7 1v12M1 7h12" stroke="#1a1408" strokeWidth="2" strokeLinecap="round" /></svg>
                  </span>
                </summary>
                <div className="flex flex-col gap-4 border-t border-[#f0e8c8] px-6 pb-6 pt-5 md:px-8 md:pb-8">
                  <p className="m-0 text-left text-base leading-[1.7] text-[#1a1408]/85">{f.a}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <nav aria-label="Explore more" className="container-x flex flex-col items-center gap-4 px-6 py-12 text-center md:px-12">
        <p className="font-sans text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">EXPLORE MORE</p>
        <div className="flex flex-wrap justify-center gap-6 font-sans">
          <Link to="/salesforce-procurement" className={linkCls}>Salesforce procurement advisory</Link>
          <Link to="/services" className={linkCls}>Advisory services</Link>
          <Link to="/dioro" className={linkCls}>Dioro</Link>
          <Link to="/case-study" className={linkCls}>PHMG case study</Link>
          <Link to="/blog" className={linkCls}>Insights</Link>
        </div>
      </nav>

      {/* CTA */}
      <section className="container-x pt-4 pb-16 md:pt-8 md:pb-24 px-6 md:px-12">
        <div className="flex flex-wrap items-center justify-between gap-6 md:gap-8 rounded-3xl bg-foreground p-6 md:p-8 text-background">
          <div className="flex flex-col gap-4 md:gap-6">
            <h3 className="max-w-xl text-3xl text-background md:text-4xl">Contact Us.</h3>
            <p className="max-w-xl text-background/70">
              Get additional negotiation help and make sure you spend your
              budget right. We'll get back to you within 24h.
            </p>
          </div>
          <Link
            to="/contact"
            style={{ color: "#1a1408" }}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 font-sans text-sm font-medium uppercase tracking-[0.15em]"
          >
            Contact now <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </>
  );
}
